// Constantes pour la feature Ephemeral Stories (stories éphémères type Snap/Insta).
// Sources : brief API REST /ephemeral-stories + conventions UX Insta.

// Le mode "public web" (valeur `guest`, visible des visiteurs non inscrits) a été retiré :
// une story n'est visible que par les membres inscrits, ou par le cercle.
const STORY_RIGHTS = {
    PUBLIC: 'public',
    FRIENDS: 'friends'
};

// État serveur des stories (cf. API : 0=staging, 1=ok, 2=encoding, 3=error)
const STORY_STATE = {
    STAGING: 0,
    OK: 1,
    ENCODING: 2,
    ERROR: 3
};

const STORY_KIND = {
    PHOTO: 'photo',
    VIDEO: 'video'
};

// Durée d'affichage d'une story photo (UX Insta)
const STORY_PHOTO_DURATION_MS = 5000;

// Limites serveur (cf. brief)
const STORY_VIDEO_MAX_DURATION_S = 300;                    // 5 min
const STORY_PHOTO_MAX_SIZE = 20 * 1024 * 1024;             // 20 Mb
const STORY_VIDEO_MAX_SIZE = 1024 * 1024 * 1024;           // 1 Gb

// `accept` du sélecteur de fichiers — identique à la publication de média (Publish) : permissif.
// C'est l'API qui valide réellement le format et le poids (415/413) ; cet `accept` ne fait que
// pré-filtrer le picker. HEIC exclu (le libvips du serveur n'a pas le codec HEVC).
const STORY_PHOTO_ACCEPT = 'image/jpeg, image/png, image/gif';
const STORY_VIDEO_ACCEPT = 'video/mp4, video/flv, video/mpeg, video/mkv, video/mov, video/qt, video/quicktime, video/wmv, video/avi';
const STORY_ALL_ACCEPT = `${STORY_PHOTO_ACCEPT}, ${STORY_VIDEO_ACCEPT}`;

// Délai avant skip auto d'une story vidéo encore en encodage
const STORY_ENCODING_SKIP_DELAY_MS = 2500;

// Libellés des droits affichés dans le sélecteur de visibilité.
// Alignés sur la convention Voissa des publications (cf. COLLECTION_VISIBILITY dans src/const/collections.js).
const STORY_RIGHT_LABELS = {
    [STORY_RIGHTS.PUBLIC]: "👥 Membres",
    [STORY_RIGHTS.FRIENDS]: "⭕ Mon cercle"
};

// Descriptions courtes pour aider l'utilisateur à choisir.
const STORY_RIGHT_DESCRIPTIONS = {
    [STORY_RIGHTS.PUBLIC]: "Visible uniquement par les membres inscrits sur Voissa.",
    [STORY_RIGHTS.FRIENDS]: "Visible uniquement par les membres de votre cercle."
};

// Ordre d'affichage : du plus large au plus restreint (convention Voissa).
const STORY_RIGHTS_ORDER = [STORY_RIGHTS.PUBLIC, STORY_RIGHTS.FRIENDS];

// Plafonds journaliers accordés par la CERTIFICATION (miroir de STORY_QUOTA dans
// voissa-api-rest, const/ephemeralStories.js). Purement DÉCLARATIF : sert la microcopy
// (« 1/jour », « 2/jour ») et rien d'autre — l'état réel du compteur arrive dans
// `currentUser.quota.story`, et l'API reste seule à décider (402). Les deux listes doivent rester
// identiques, sinon on annonce un plafond qui n'est pas celui appliqué.
const STORY_QUOTA = {
    PUBLISH_PER_DAY: 1,
    VIEW_PER_DAY: 2,
};

// Durées de VIE d'une story, en secondes — miroir de STORY_DURATIONS côté API. À ne pas confondre
// avec la durée de la VIDÉO : le champ envoyé s'appelle `expiresIn`, celui de la vidéo `duration`.
// 48 h est réservé aux abonnés et leur est proposé par DÉFAUT (à eux de redescendre à 24 h) ; les
// autres voient l'option mais la sélectionner ouvre la modale d'abonnement. Rien en dessous de
// 24 h : on manque de stories, et des durées plus courtes compliqueraient la mise en avant.
const STORY_DURATIONS = {
    TWENTY_FOUR_HOURS: 24 * 60 * 60,
    FORTY_EIGHT_HOURS: 48 * 60 * 60
};
const STORY_DURATIONS_ORDER = [STORY_DURATIONS.TWENTY_FOUR_HOURS, STORY_DURATIONS.FORTY_EIGHT_HOURS];
const STORY_DURATION_LABELS = {
    [STORY_DURATIONS.TWENTY_FOUR_HOURS]: '24 heures',
    [STORY_DURATIONS.FORTY_EIGHT_HOURS]: '48 heures'
};
const STORY_DURATION_DESCRIPTIONS = {
    [STORY_DURATIONS.TWENTY_FOUR_HOURS]: 'Votre story disparaît au bout de 24 heures.',
    [STORY_DURATIONS.FORTY_EIGHT_HOURS]: 'Votre story reste visible deux fois plus longtemps.'
};
// Palier nécessitant un abonnement (capability pleine `canCreateStory`).
const STORY_DURATION_PREMIUM = STORY_DURATIONS.FORTY_EIGHT_HOURS;
const STORY_DURATION_DEFAULT = STORY_DURATIONS.TWENTY_FOUR_HOURS;

// Sujet CASL côté backend.
const STORY_ABILITY_SUBJECT = 'EphemeralStory';
// Action "enable" : la feature est activée pour ce user (gate statique, ex: admin-only en v1).
// Détermine si le CTA de création est visible.
const STORY_ABILITY_ENABLE_ACTION = 'enable';
// Action "create" : le user peut effectivement créer une story maintenant (gate dynamique,
// peut inclure des conditions runtime côté back). Check au submit final.
const STORY_ABILITY_CREATE_ACTION = 'create';
// Action "moderate" : accorde aux admins le droit de supprimer une story qui n'est pas la leur,
// et de consulter likes/vues des stories des autres. Le owner conserve ces droits via la propriété.
// Si backend ne livre pas encore la règle, le check retourne false → seul l'owner voit ces options.
const STORY_ABILITY_MODERATE_ACTION = 'moderate';

export {
    STORY_RIGHTS,
    STORY_STATE,
    STORY_KIND,
    STORY_PHOTO_DURATION_MS,
    STORY_VIDEO_MAX_DURATION_S,
    STORY_PHOTO_MAX_SIZE,
    STORY_VIDEO_MAX_SIZE,
    STORY_PHOTO_ACCEPT,
    STORY_VIDEO_ACCEPT,
    STORY_ALL_ACCEPT,
    STORY_ENCODING_SKIP_DELAY_MS,
    STORY_QUOTA,
    STORY_DURATIONS,
    STORY_DURATIONS_ORDER,
    STORY_DURATION_LABELS,
    STORY_DURATION_DESCRIPTIONS,
    STORY_DURATION_PREMIUM,
    STORY_DURATION_DEFAULT,
    STORY_RIGHT_LABELS,
    STORY_RIGHT_DESCRIPTIONS,
    STORY_RIGHTS_ORDER,
    STORY_ABILITY_SUBJECT,
    STORY_ABILITY_ENABLE_ACTION,
    STORY_ABILITY_CREATE_ACTION,
    STORY_ABILITY_MODERATE_ACTION
};
